import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ManagementTableColumn } from "../shared/model/customers-table-column.model";
import {
  CustomersResponseModel,
  MobileUser,
  PartialOrderData,
  TransformedOrder,
  TransformedUser,
} from "../shared/model/customers-response.model";
import { AdminToolPanel } from "../../pages/orders/my/tool-panel/admin-tool-panel";
import { CustomerFilterModel } from "../../pages/orders/my/tool-panel/filter/customer-filter";
import { WarehousesResponseModel } from "../../pages/shared/models/api/warehouses-response.model";
import ManagementTable from "../management-table/table/management-table";
import { ManagementActionBar } from "./blocks/management-action-bar";
import { AllMessagesResponseModel } from "../../pages/shared/models/api/all-messages-response.model";
import { User } from "../../pages/shared/models/user/user";
import { AllUsersResponseModel } from "../../pages/shared/models/api/all-users-response.model";
import { StandardResponseModel } from "../../pages/shared/models/standard-response-model";
import { TabType } from "../shared/enums";
import moment from "moment/moment";
import ExcelJS from "exceljs";
import { getMessage } from "../../pages/shared/helper-functions";
import { MessageSections } from "../../pages/components/message/message";
import { useAppSelector } from "../../redux/store";
import { OrderModel } from "../../pages/shared/models/order/order-item.model";
import { UserGroups } from "../../router/router";

export const ManagementDataContainer = ({
  columns,
  defaultFilter,
  actionbarMessageKey,
  searchFunction,
  type,
  withBackButton,
  staticDataType,
}: CustomersTableProps) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [filter, setFilter] = useState<CustomerFilterModel>({});
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get('page') && searchParams.get('page') !== '0' ? parseInt(searchParams.get('page') || '0') - 1 : 0;

  const [customersResponseData, setCustomersResponseData] = useState({
    currentPage,
    data: [],
    pageSize: 10,
    totalItems: 0,
    overallItems: 0,
  } as CustomersResponseModel);

  useEffect(() => {
    setCustomersResponseData({
      currentPage,
      data: [],
      pageSize: 10,
      totalItems: 0,
      overallItems: 0,
    });

    searchFunction(
      { ...filter, ...defaultFilter },
      customersResponseData.pageSize,
      currentPage,
    ).then((response) => {
      handleResponse(response);
      setIsReady(true);
    });
  }, [filter, currentPage]);

  function handlePageChange(event: any, newPage: number): void {
    searchFunction(
      { ...filter, ...defaultFilter },
      customersResponseData.pageSize,
      newPage - 1,
    ).then((response) => {
      handleResponse(response);
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', `${newPage}`);
      setSearchParams(params, { replace: true });
    });
  }

  function handleResponse(
    response:
      | CustomersResponseModel
      | AllUsersResponseModel
      | WarehousesResponseModel
      | AllMessagesResponseModel,
  ) {
    if (type === ManagementPageType.WAREHOUSE_WORKERS) {
      const allUsersResponse = response as AllUsersResponseModel;

      setCustomersResponseData({
        currentPage: allUsersResponse.currentPage,
        data: allUsersResponse.users.map((userData: User) => ({
          user: userData,
        })),
        pageSize: allUsersResponse.pageSize,
        totalItems: allUsersResponse.totalItems,
      });
    } else if (type === ManagementPageType.WAREHOUSES) {
      const warehousesResponse = response as WarehousesResponseModel;

      setCustomersResponseData({
        currentPage: warehousesResponse.currentPage,
        data: warehousesResponse.warehouses,
        pageSize: warehousesResponse.pageSize,
        totalItems: warehousesResponse.totalItems,
      });
    } else if (type === ManagementPageType.CUSTOMERS) {
      setCustomersResponseData(response as CustomersResponseModel);
    } else if (type === ManagementPageType.MESSAGES) {
      const allMessagesResponse = response as AllMessagesResponseModel;

      setCustomersResponseData({
        currentPage: allMessagesResponse.currentPage,
        data: allMessagesResponse.messages,
        pageSize: allMessagesResponse.pageSize,
        totalItems: allMessagesResponse.totalItems,
      });
    } else if (type === ManagementPageType.STATIC_VALUES) {
      setCustomersResponseData(response as StandardResponseModel<any>);
    }
    else if (type === ManagementPageType.MOBILE_USER) {
      const allUsersResponse = response as CustomersResponseModel;
      setCustomersResponseData({
        currentPage: allUsersResponse.currentPage,
        data: allUsersResponse.data.map((userData: MobileUser): TransformedUser => ({
          dateCreated: userData?.createdAt,
          country: userData?.addresses?.[0]?.country,
          city: userData?.addresses?.[0]?.city,
          address: userData?.addresses?.[0]?.address,
          firstName: userData?.firstName,
          lastName: userData?.lastName,
          mobileNumber: userData?.mobileNumber,
          email: userData?.email,
          quantity: userData?.orders?.length || 0,
          mobileUser: true,
        })),
        pageSize: allUsersResponse.pageSize,
        totalItems: allUsersResponse.totalItems,
        overallItems: allUsersResponse?.overallItems,
      });
    }
    else if (type === ManagementPageType.MOBILE_ORDERS) {
      const allUsersResponse = response as CustomersResponseModel;
      setCustomersResponseData({
        currentPage: allUsersResponse.currentPage,
        data: allUsersResponse.data.map((userData: any): TransformedOrder => ({
          dateCreated: userData?.createdAt,
          city: userData?.orderAddress?.city,
          country: userData?.orderAddress?.country,
          zipCode: userData?.orderAddress?.postalCode,
          address: userData?.orderAddress?.address,
          firstName: userData?.relatedUser?.firstName,
          lastName: userData?.relatedUser?.lastName,
          mobileNumber: userData?.relatedUser?.mobileNumber,
          email: userData?.relatedUser?.email,
          orderWeight: userData?.orderWeight || 0,
          quantity: userData?.quantity || 0,
          orders: userData?.relatedUser?.orders.length || 0,
          orderId: userData.id,
          state: userData?.state,
          mobileOrder: true,
        })),
        pageSize: allUsersResponse.pageSize,
        totalItems: allUsersResponse.totalItems,
        overallItems: allUsersResponse?.overallItems,
      });
    }
  }

  function handleFilterChange(filter: CustomerFilterModel) {
    setFilter(filter);
  }

  async function onExport() {
    const currentDate = moment().format("DD-MM-YYYY");
    const currentDateTime = moment().format("DD-MM-YYYY-HH:mm");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(currentDate);
    worksheet.addRow([
      "Name",
      "Barekam ID",
      "Total Orders Count",
      "Total Weight",
      "Total Payments",
      "Balance",
      "Email",
      "Mobile Number",
      "Document ID",
      "Registration Date",
      "Status",
    ]);

    searchFunction(
      { ...filter, ...defaultFilter },
      defaultFilter?.userGroup === UserGroups.BAREKAM ? -1 : customersResponseData.pageSize,
      currentPage,
    ).then(async(response) => {
      const customers = defaultFilter?.userGroup === UserGroups.BAREKAM ? (response as CustomersResponseModel) : customersResponseData;

      if (customers && customers?.data?.length > 0) {
        customers.data.forEach((customer) => {
          worksheet.addRow([
            `${customer.user?.firstName} ${customer.user?.lastName}`,
            `${customer.user?.addressLine2ForAir}, ${customer.user?.addressLine2ForLand}`,
            customer?.orders?.length || 0,
            customer?.orders?.map((order: OrderModel) => order.weight || 0)
              .reduce(
                (previousValue: number, currentValue: number) =>
                  previousValue + currentValue,
                0,
              ) || 0,
            customer?.orders?.reduce(
              (count: number, order: PartialOrderData) =>
                count + order.paymentInfo,
              0,
            ) || 0,
            customer.user?.balance || 0,
            customer.user?.email || '',
            customer.user?.phoneNumber || '',
            customer.user?.passportOrIdSerialNumber || '',
            moment(customer.user?.dateCreated).format("DD-MM-YYYY"),
            customer.user?.status?.name,
          ]);
        });
      }

      // Exporting the workbook
      const buffer = await workbook.xlsx.writeBuffer();
      const fileSizeInBytes = buffer.byteLength;

      // Check if the file size exceeds a certain limit (e.g., 5MB)
      const maxFileSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (fileSizeInBytes > maxFileSizeInBytes) {
        alert(
          getMessage(
            "file_size_exceeds_5_mb_limit",
            MessageSections.MAIN,
            messageCache,
            languageId,
          ),
        );
      } else {
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `customers-${currentDateTime}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  if (isReady) {
    return (
      <>
        {type !== ManagementPageType.STATIC_VALUES && (
          <ManagementActionBar
            actionbarMessageKey={actionbarMessageKey}
            type={type}
            withBackButton={withBackButton}
            overallItems={customersResponseData?.overallItems}
          />
        )}

        <AdminToolPanel
          type={type}
          filter={filter}
          exportFunction={onExport}
          setFilter={(filter) => handleFilterChange(filter)}
        />

        <ManagementTable
          type={type}
          columns={columns}
          customersData={customersResponseData}
          handlePageChange={handlePageChange}
          staticDataType={staticDataType}
        />
      </>
    );
  } else {
    return <></>;
  }
};

interface CustomersTableProps {
  type: ManagementPageType;
  columns: ManagementTableColumn[];
  defaultFilter: any;
  searchFunction: (
    filter: any,
    limit?: number,
    page?: number,
  ) => Promise<
    | CustomersResponseModel
    | AllUsersResponseModel
    | WarehousesResponseModel
    | StandardResponseModel<any>
  >;
  staticDataType?: TabType;
  actionbarMessageKey?: string;
  withBackButton?: boolean;
  refresh?: boolean;
}

export enum ManagementPageType {
  CUSTOMERS,
  WAREHOUSES,
  WAREHOUSE_WORKERS,
  ALL_ORDERS,
  MESSAGES,
  STATIC_VALUES,
  HISTORY,
  MOBILE_USER,
  MOBILE_ORDERS
}
